import { Effect, Layer } from 'effect';

import { BlobStore, BlobStoreError } from './blob-store.js';
import { DocumentGraphStore, DocumentGraphStoreError } from './document-graph-store.js';
import { IndexStore, IndexStoreError } from './index-store.js';
import { SqlExecutor, SqlExecutorError } from './sql-executor.js';

function assertContract(condition: boolean, message: string): asserts condition {
  if (!condition) throw new Error(message);
}

function assertBytes(actual: Uint8Array, expected: Uint8Array, message: string): void {
  assertContract(actual.byteLength === expected.byteLength, `${message}: byte length differs`);
  for (let index = 0; index < expected.byteLength; index += 1) {
    assertContract(actual[index] === expected[index], `${message}: byte ${index} differs`);
  }
}

/** Runtime-neutral BlobStore contract. Test packages can invoke it with any concrete layer. */
export async function blobStoreContractSuite<E>(
  name: string,
  layer: Layer.Layer<BlobStore, E>,
): Promise<void> {
  const program = Effect.gen(function*() {
    const store = yield* BlobStore;
    const missing = yield* store.get('missing');
    assertContract(missing === undefined, `${name}: a missing key must return undefined`);
    assertContract((yield* store.head('missing')) === undefined, `${name}: missing head must be undefined`);

    const pdf = new Uint8Array([0x25, 0x50, 0x44, 0x46, 0x00, 0xff]);
    yield* store.put('docs/zeta.pdf', pdf, 'application/pdf');
    yield* store.put('docs/alpha.txt', new TextEncoder().encode('alpha'), 'text/plain');
    yield* store.put('outside.bin', new Uint8Array([7]), 'application/octet-stream');

    const stored = yield* store.get('docs/zeta.pdf');
    assertContract(stored !== undefined, `${name}: stored bytes must be readable`);
    assertBytes(stored.bytes, pdf, `${name}: stored bytes must round-trip`);
    assertContract(stored.contentType === 'application/pdf', `${name}: content type must round-trip`);

    const head = yield* store.head('docs/zeta.pdf');
    assertContract(head?.size === pdf.byteLength, `${name}: head must expose byte size`);
    assertContract(head.contentType === 'application/pdf', `${name}: head must expose content type`);

    const listed = yield* store.list('docs/');
    assertContract(listed.length === 2, `${name}: prefix listing must exclude other keys`);
    assertContract(
      listed[0]?.key === 'docs/alpha.txt' && listed[1]?.key === 'docs/zeta.pdf',
      `${name}: prefix listing must be ordered by key`,
    );

    yield* store.delete('docs/zeta.pdf');
    assertContract((yield* store.get('docs/zeta.pdf')) === undefined, `${name}: delete must remove a key`);
    yield* store.delete('docs/zeta.pdf');

    const error = yield* Effect.flip(store.get(''));
    assertContract(error instanceof BlobStoreError, `${name}: failures must use BlobStoreError`);
    assertContract(error._tag === 'BlobStoreError', `${name}: error tag must remain inspectable`);
    assertContract(error.method === 'get' && error.key === '', `${name}: errors must identify the operation`);
  }).pipe(Effect.provide(layer));

  await Effect.runPromise(program);
}

/** Runtime-neutral SqlExecutor contract. Test packages can invoke it with any concrete layer. */
export async function sqlExecutorContractSuite<E>(
  name: string,
  layer: Layer.Layer<SqlExecutor, E>,
): Promise<void> {
  const program = Effect.gen(function*() {
    const sql = yield* SqlExecutor;
    yield* sql.exec('DROP TABLE IF EXISTS contract_items');
    yield* sql.exec('CREATE TABLE contract_items (id INTEGER PRIMARY KEY, value TEXT NOT NULL)');
    yield* sql.exec('INSERT INTO contract_items (id, value) VALUES (?, ?)', 2, 'beta');
    yield* sql.exec('INSERT INTO contract_items (id, value) VALUES (?, ?)', 1, 'alpha');

    const rows = yield* sql.exec<{ readonly id: number; readonly value: string }>(
      'SELECT id, value FROM contract_items ORDER BY id',
    );
    assertContract(rows.length === 2, `${name}: exec must materialize every cursor row`);
    assertContract(rows[0]?.value === 'alpha' && rows[1]?.value === 'beta', `${name}: bound rows must round-trip`);

    const one = yield* sql.one<{ readonly id: number; readonly value: string }>(
      'SELECT id, value FROM contract_items WHERE id = ?',
      2,
    );
    assertContract(one?.value === 'beta', `${name}: one must return the matching row`);
    assertContract(
      (yield* sql.one('SELECT id, value FROM contract_items WHERE id = ?', 999)) === undefined,
      `${name}: one must return undefined for no rows`,
    );

    const error = yield* Effect.flip(sql.exec('THIS IS NOT SQL'));
    assertContract(error instanceof SqlExecutorError, `${name}: failures must use SqlExecutorError`);
    assertContract(error._tag === 'SqlExecutorError', `${name}: error tag must remain inspectable`);
    assertContract(error.method === 'exec', `${name}: SQL errors must identify the operation`);
  }).pipe(Effect.provide(layer));

  await Effect.runPromise(program);
}

/** Runtime-neutral DocumentGraphStore contract over any SqlExecutor-backed layer. */
export async function documentGraphStoreContractSuite<E>(
  name: string,
  layer: Layer.Layer<DocumentGraphStore, E>,
): Promise<void> {
  const program = Effect.gen(function*() {
    const store = yield* DocumentGraphStore;

    assertContract((yield* store.getMetaValue('missing')) === null, `${name}: missing meta must be null`);
    yield* store.setMeta('total_pages', '2');
    yield* store.setMeta('pass1_vendor', 'contract-vendor');
    yield* store.setMeta('reparse_generation', '3');
    assertContract(
      (yield* store.getMetaValue('pass1_vendor')) === 'contract-vendor',
      `${name}: meta values must round-trip`,
    );

    const inserted = yield* store.insertNode(
      {
        id: 'contract-node',
        parent_id: null,
        type: 'text',
        label: 'Contract label',
        value: 'Contract value',
        status: 'verified',
        page_number: 1,
        confidence: 0.95,
        metadata: null,
        bbox: { x: 2, y: 0.1, w: 0.2, h: 0.3 },
      },
      true,
    );
    assertContract(inserted.bboxDropped, `${name}: out-of-range bbox values must be dropped`);

    const node = yield* store.queryRows<{
      readonly id: string;
      readonly status: string;
      readonly confidence: number | null;
      readonly bbox_x: number | null;
      readonly bbox_y: number | null;
    }>('SELECT id, status, confidence, bbox_x, bbox_y FROM nodes WHERE id = ?', 'contract-node');
    assertContract(node[0]?.bbox_x === null && node[0]?.bbox_y === null, `${name}: dropped bbox must persist as null`);

    const indexed = yield* store.queryRows<{ readonly node_id: string; readonly value: string }>(
      'SELECT node_id, label, value, type FROM nodes_fts WHERE node_id = ?',
      'contract-node',
    );
    assertContract(indexed[0]?.value === 'Contract value', `${name}: searchable nodes must enter FTS`);

    yield* store.insertEdge('contract-node', 'contract-target', 'contains');
    const edges = yield* store.queryRows<{
      readonly source_id: string;
      readonly target_id: string;
      readonly relation: string;
    }>('SELECT source_id, target_id, relation FROM edges WHERE source_id = ?', 'contract-node');
    assertContract(
      edges[0]?.target_id === 'contract-target' && edges[0]?.relation === 'contains',
      `${name}: edges must round-trip`,
    );

    const initialFingerprint = yield* store.getStateFingerprint();
    yield* store.appendDocLog('contract-first', 'system', 'contract', 'contract-node', { value: 1 });
    const firstFingerprint = yield* store.getStateFingerprint();
    yield* store.appendDocLog('contract-second', 'system', 'contract', 'contract-node', { value: 2 });
    const secondFingerprint = yield* store.getStateFingerprint();
    assertContract(
      initialFingerprint.length === 64
        && firstFingerprint.length === 64
        && secondFingerprint.length === 64
        && initialFingerprint !== firstFingerprint
        && firstFingerprint !== secondFingerprint,
      `${name}: document-log changes must advance the SHA-256 state fingerprint`,
    );

    const finding = yield* store.logMutation(
      'contract-node',
      'update',
      'human',
      'contract-user',
      'old',
      'new',
      { nodeType: 'text' },
    );
    assertContract(finding?.ruleId === 'confidence_decay', `${name}: human edits must create confidence decay`);

    const decayed = yield* store.queryRows<{
      readonly status: string;
      readonly confidence: number | null;
    }>('SELECT id, status, confidence, bbox_x, bbox_y FROM nodes WHERE id = ?', 'contract-node');
    assertContract(
      decayed[0]?.status === 'needs_review' && decayed[0]?.confidence === null,
      `${name}: human edits must reset confidence and review status`,
    );
    const findingRows = yield* store.queryRows<{ readonly rule_id: string }>(
      'SELECT id, node_id, rule_id, severity, message FROM findings WHERE node_id = ?',
      'contract-node',
    );
    assertContract(findingRows[0]?.rule_id === 'confidence_decay', `${name}: confidence finding must persist`);

    const status = yield* store.computeStatus();
    assertContract(status.totalNodes === 1, `${name}: status must count non-page nodes`);
    assertContract(status.pendingNodes === 1, `${name}: status must count needs-review nodes`);
    assertContract(
      status.pageCount === 2 && status.pagesPending === 2 && status.pageCoverage === 'empty',
      `${name}: status must derive page coverage from metadata`,
    );
    assertContract(status.activeVendor === 'contract-vendor', `${name}: status must expose active vendor`);

    const rebuilt = yield* store.rebuildFtsIndex();
    assertContract(rebuilt.count === 1, `${name}: FTS rebuild must index searchable nodes`);

    const vendorRawSeq = yield* store.storeVendorRaw({
      vendorLogSeq: 7,
      pageNumber: 1,
      contentType: 'application/json',
      rawResponse: '{"ok":true}',
      bytes: 11,
      r2StorageKey: 'vendor-raw/contract/7-p1.json',
    });
    assertContract(vendorRawSeq > 0, `${name}: vendor-raw insert must return its sequence`);
    const vendorRaw = yield* store.queryRows<{
      readonly vendor_log_seq: number;
      readonly raw_response: string;
      readonly r2_storage_key: string | null;
    }>(
      'SELECT vendor_log_seq, raw_response, r2_storage_key FROM vendor_raw WHERE seq = ?',
      vendorRawSeq,
    );
    assertContract(
      vendorRaw[0]?.vendor_log_seq === 7
        && vendorRaw[0]?.r2_storage_key === 'vendor-raw/contract/7-p1.json',
      `${name}: vendor-raw metadata must round-trip`,
    );

    yield* store.recordCost('contract-operation', 'contract-vendor', 0.25, {
      model: 'contract-model',
      inputTokens: 10,
      outputTokens: 5,
      nodeId: 'contract-node',
      batchId: 'contract-batch',
    });
    const costs = yield* store.queryRows<{ readonly event: string; readonly detail: string }>(
      "SELECT event, detail FROM document_log WHERE event = 'cost' ORDER BY seq DESC LIMIT 1",
    );
    const costDetail = JSON.parse(costs[0]?.detail ?? '{}') as Record<string, unknown>;
    assertContract(
      costs[0]?.event === 'cost' && costDetail.costUsd === 0.25 && costDetail.generation === 3,
      `${name}: cost records must include amount and reparse generation`,
    );

    const error = yield* Effect.flip(store.queryRows('THIS IS NOT SQL'));
    assertContract(
      error instanceof DocumentGraphStoreError,
      `${name}: failures must use DocumentGraphStoreError`,
    );
    assertContract(error._tag === 'DocumentGraphStoreError', `${name}: error tag must remain inspectable`);
    assertContract(error.method === 'queryRows', `${name}: errors must identify the domain operation`);
  }).pipe(Effect.provide(layer));

  await Effect.runPromise(program);
}

/** Runtime-neutral IndexStore contract over any SqlExecutor-backed layer. */
export async function indexStoreContractSuite<E>(
  name: string,
  layer: Layer.Layer<IndexStore, E>,
): Promise<void> {
  const program = Effect.gen(function*() {
    const store = yield* IndexStore;

    assertContract(
      (yield* store.getCollection('missing')) === undefined,
      `${name}: missing collections must be undefined`,
    );

    yield* store.exec(
      `INSERT INTO collections (id, user_id, name, slug, description, visibility, cloned_from, allow_clone, webhook_url, extraction_schema, created_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      'col-contract',
      'user-1',
      'Contract',
      'contract',
      null,
      'public',
      null,
      0,
      null,
      null,
      '2026-08-27T00:00:00.000Z',
    );

    const created = yield* store.getCollection('col-contract');
    assertContract(created?.name === 'Contract', `${name}: collections must round-trip`);
    assertContract(created.visibility === 'public', `${name}: collection visibility must persist`);

    yield* store.exec(
      `INSERT INTO documents (doc_id, file_name, phase, pages_total, total_nodes, source, is_public)
       VALUES (?, ?, ?, ?, ?, ?, ?)`,
      'doc-public',
      'Public.pdf',
      'complete',
      10,
      100,
      'upload',
      1,
    );
    yield* store.exec(
      `INSERT INTO documents (doc_id, file_name, phase, pages_total, total_nodes, source, is_public)
       VALUES (?, ?, ?, ?, ?, ?, ?)`,
      'doc-private',
      'Private.pdf',
      'complete',
      20,
      200,
      'upload',
      0,
    );
    yield* store.exec(
      'INSERT INTO collection_documents (collection_id, document_id, added_at) VALUES (?, ?, ?)',
      'col-contract',
      'doc-public',
      '2026-04-01T00:00:00.000Z',
    );
    yield* store.exec(
      'INSERT INTO collection_documents (collection_id, document_id, added_at) VALUES (?, ?, ?)',
      'col-contract',
      'doc-private',
      '2026-04-02T00:00:00.000Z',
    );

    const allIds = yield* store.listCollectionDocumentIds('col-contract');
    assertContract(allIds.length === 2, `${name}: membership listing must include every document`);
    const publicIds = yield* store.listCollectionDocumentIds('col-contract', { publicOnly: true });
    assertContract(
      publicIds.length === 1 && publicIds[0] === 'doc-public',
      `${name}: public-only listing must hide private documents`,
    );

    const publicDocs = yield* store.listCollectionDocuments('col-contract', { publicOnly: true });
    assertContract(
      publicDocs.length === 1
        && publicDocs[0]?.document_id === 'doc-public'
        && publicDocs[0]?.file_name === 'Public.pdf',
      `${name}: public document details must join collection membership`,
    );

    const count = yield* store.countCollectionDocuments('col-contract');
    assertContract(count === 2, `${name}: document counts must include every membership`);

    const queried = yield* store.queryRows<{ readonly id: string; readonly name: string }>(
      'SELECT id, name FROM collections WHERE id = ?',
      'col-contract',
    );
    assertContract(queried[0]?.name === 'Contract', `${name}: queryRows must return matching rows`);
    const one = yield* store.one<{ readonly name: string }>(
      'SELECT name FROM collections WHERE id = ?',
      'col-contract',
    );
    assertContract(one?.name === 'Contract', `${name}: one must return the matching row`);
    assertContract(
      (yield* store.one('SELECT name FROM collections WHERE id = ?', 'missing')) === undefined,
      `${name}: one must return undefined for no rows`,
    );

    const error = yield* Effect.flip(store.queryRows('THIS IS NOT SQL'));
    assertContract(error instanceof IndexStoreError, `${name}: failures must use IndexStoreError`);
    assertContract(error._tag === 'IndexStoreError', `${name}: error tag must remain inspectable`);
    assertContract(error.method === 'queryRows', `${name}: errors must identify the domain operation`);
  }).pipe(Effect.provide(layer));

  await Effect.runPromise(program);
}
